import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogIn } from "lucide-react";
import { LINKS, IMAGES } from "@/lib/assets";
import { useSettings } from "@/lib/SettingsContext";
import { isPromoActive } from "@/lib/promo";

const NAV = [
  { label: "About", href: "#about" },
  { label: "Benefits", href: "#benefits" },
  { label: "Membership", href: "#membership" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onDark = !scrolled;
  const promoActive = isPromoActive(useSettings().promo);
  const logoText = onDark ? "text-white" : "text-navy-dark";
  const subText = onDark ? "text-white/60" : "text-zinc-500";
  const linkClass = onDark
    ? "text-white/85 hover:text-white"
    : "text-zinc-500 hover:text-amber-500";
  const outlineBtn = onDark
    ? "border-white/30 text-white hover:border-white"
    : "border-navy/25 text-navy-dark hover:border-amber-500 hover:text-amber-500";

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed left-0 w-full z-50 transition-all duration-300 ${promoActive ? "top-10" : "top-0"} ${
        scrolled ? "backdrop-blur-xl bg-white/85 border-b border-slate-200 shadow-[0_1px_24px_rgba(15,23,42,0.06)]" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3 group" data-testid="navbar-logo">
          <img src={IMAGES.logo} alt="NTOA" className="h-11 w-11 object-contain" />
          <div className="hidden sm:block leading-none">
            <div className={`font-display font-black text-lg tracking-tight ${logoText}`}>NTOA</div>
            <div className={`text-[9px] tracking-[0.12em] uppercase whitespace-nowrap ${subText}`}>National Transportation Operators Association</div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <a
              key={n.label}
              href={n.href}
              className={`text-sm font-medium transition-colors ${linkClass}`}
              data-testid={`nav-${n.label.toLowerCase()}`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={LINKS.login}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md border text-sm font-semibold transition-colors ${outlineBtn}`}
            data-testid="nav-login-btn"
          >
            <LogIn size={16} /> Log In
          </a>
          <a
            href={LINKS.signup}
            className="px-5 py-2.5 rounded-md bg-amber-500 text-white text-sm font-bold hover:bg-amber-600 transition-colors"
            data-testid="nav-join-btn"
          >
            Join NTOA
          </a>
        </div>

        <button
          className={`md:hidden ${logoText}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-slate-200"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((n) => (
                <a
                  key={n.label}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  className="text-base font-medium text-zinc-400 hover:text-amber-500"
                  data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </a>
              ))}
              <div className="flex gap-3 pt-2">
                <a href={LINKS.login} className="flex-1 text-center px-4 py-3 rounded-md border border-navy/25 text-navy-dark text-sm font-semibold">
                  Log In
                </a>
                <a href={LINKS.signup} className="flex-1 text-center px-4 py-3 rounded-md bg-amber-500 text-white text-sm font-bold">
                  Join NTOA
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
